import * as THREE from 'three';

/**
 * The train, drawn.
 *
 * `src/core/train.js` decides where it is and what phase it is in; this is the
 * part with meshes in it. The split is the same one the arena keeps: the rule
 * layer owns the footprint the collision uses, and nothing here is allowed to
 * be the authority on where the train actually is.
 *
 * Two things are on screen before the train is:
 *
 * - **Headlights in the tunnel mouth.** Two spot lights and a glowing disc at
 *   whichever end the service is coming from, brought up over the telegraph.
 *   This is the warning, and it has to be visible from the middle of the
 *   platform with your back to it — which is why it also throws light *onto*
 *   the platform rather than just glowing in the dark.
 * - **The rumble.** No audio layer exists yet, so the cue hooks are wired to
 *   nothing; what is visible is the light.
 */

const CARRIAGE_GAP = 0.35;

export function createSubwayTrain(train, { pit, palette = {} } = {}) {
  const disposables = [];
  const { length, width, z } = train.config;

  // Both rigs sit on the track's centre line, and everything inside them is
  // built in their own local space. Only `x` is ever written afterwards, so
  // there is exactly one place the train's z lives.
  const group = new THREE.Group();
  group.name = 'subway-train';
  group.visible = false;
  group.position.z = z;
  const height = 2.9;
  const floor = pit?.floor ?? -1.1;

  function material(options) {
    const made = new THREE.MeshStandardMaterial(options);
    disposables.push(made);
    return made;
  }

  const bodyMaterial = material({
    color: new THREE.Color(palette.trainBody ?? '#9aa3ae'),
    roughness: 0.4,
    metalness: 0.6,
  });
  const skirtMaterial = material({
    color: new THREE.Color(palette.trainSkirt ?? '#2a2d34'),
    roughness: 0.8,
    metalness: 0.3,
  });
  const windowMaterial = material({
    color: new THREE.Color(palette.trainWindow ?? '#cfe6ff'),
    emissive: new THREE.Color(palette.trainWindow ?? '#cfe6ff'),
    emissiveIntensity: 1.1,
    roughness: 0.2,
    metalness: 0,
  });

  // Three carriages rather than one long box: the gaps are what make it read as
  // a train going past rather than as a wall sliding by.
  const carriages = 3;
  const carriageLength = (length - CARRIAGE_GAP * (carriages - 1)) / carriages;
  const bodyGeometry = new THREE.BoxGeometry(carriageLength, height * 0.72, width);
  const skirtGeometry = new THREE.BoxGeometry(carriageLength, height * 0.28, width * 0.92);
  const windowGeometry = new THREE.BoxGeometry(carriageLength * 0.82, 0.52, width + 0.04);
  const roofGeometry = new THREE.BoxGeometry(carriageLength * 0.45, 0.16, width * 0.5);
  const ribGeometry = new THREE.BoxGeometry(carriageLength * 0.92, 0.08, 0.12);
  disposables.push(bodyGeometry, skirtGeometry, windowGeometry, roofGeometry, ribGeometry);

  const roofMaterial = material({
    color: new THREE.Color(palette.trainRoof ?? '#4c525c'),
    roughness: 0.85,
    metalness: 0.2,
  });

  for (let i = 0; i < carriages; i++) {
    const offset = -length / 2 + carriageLength / 2 + i * (carriageLength + CARRIAGE_GAP);
    const bodyY = floor + height * 0.28 + (height * 0.72) / 2;

    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.set(offset, bodyY, 0);
    body.castShadow = true;
    group.add(body);

    const skirt = new THREE.Mesh(skirtGeometry, skirtMaterial);
    skirt.position.set(offset, floor + (height * 0.28) / 2, 0);
    group.add(skirt);

    const windows = new THREE.Mesh(windowGeometry, windowMaterial);
    windows.position.set(offset, floor + height * 0.72, 0);
    group.add(windows);

    // Roof gear. The roof is the part of the train the camera sees most of, and
    // a flat slab passing through the bottom of the frame reads as a wall
    // sliding by rather than as a train.
    const pod = new THREE.Mesh(roofGeometry, roofMaterial);
    pod.position.set(offset, floor + height + 0.06, 0);
    group.add(pod);
    for (const side of [-1, 1]) {
      const rib = new THREE.Mesh(ribGeometry, skirtMaterial);
      rib.position.set(offset, floor + height + 0.02, side * (width / 2 - 0.22));
      group.add(rib);
    }
  }

  // The lit interior, as one light per carriage. It is what makes a train
  // passing change the light on the platform, which is most of the drama.
  const cabin = new THREE.PointLight(
    new THREE.Color(palette.trainWindow ?? '#cfe6ff'),
    12,
    9,
    2
  );
  cabin.position.set(0, floor + height * 0.7, 0);
  group.add(cabin);

  // --- the telegraph --------------------------------------------------------

  const headlights = new THREE.Group();
  headlights.name = 'train-headlights';
  headlights.visible = false;
  headlights.position.z = z;

  const lampGeometry = new THREE.CircleGeometry(0.22, 16);
  disposables.push(lampGeometry);
  const lampMaterial = material({
    color: new THREE.Color(palette.headlight ?? '#fff4d0'),
    emissive: new THREE.Color(palette.headlight ?? '#fff4d0'),
    emissiveIntensity: 3,
    toneMapped: false,
  });
  const lamps = [];
  for (const side of [-1, 1]) {
    const lamp = new THREE.Mesh(lampGeometry, lampMaterial);
    lamp.position.set(0, floor + 1.5, side * (width / 2 - 0.35));
    lamps.push(lamp);
    headlights.add(lamp);
  }

  // A spot rather than a point light: a headlight is a beam down a tunnel, and
  // it has to land on the platform for the warning to be worth anything.
  const beam = new THREE.SpotLight(
    new THREE.Color(palette.headlight ?? '#fff4d0'),
    0,
    30,
    0.62,
    0.5,
    1.15
  );
  beam.position.set(0, floor + 1.6, 0);
  // Aimed down the tunnel and *across* onto the platform, so the warning lands
  // somewhere a fighter with their back to it can still see it.
  beam.target.position.set(8, -0.6, -3.2);
  headlights.add(beam, beam.target);

  // The carriages and the headlights move independently — the lights are at the
  // tunnel mouth before there is a train to hang them on — so they are siblings
  // under one rig rather than one parented to the other.
  const scene = new THREE.Group();
  scene.name = 'subway-train-rig';
  scene.add(group, headlights);

  return {
    object3D: scene,
    train,

    /**
     * Follows the state machine. Called every frame, and it does nothing but
     * read — the train's position is `train.position`, and this module is never
     * allowed to be the one that decides it.
     */
    update() {
      const present = train.present;
      group.visible = present;
      if (present) group.position.x = train.position;

      const glow = train.glow;
      headlights.visible = glow > 0.001;
      if (!headlights.visible) return;

      // At the mouth of whichever tunnel the service is coming out of, aimed
      // down the platform, until the train itself is here to carry them.
      const mouth = train.direction > 0 ? (pit?.minX ?? -8) : (pit?.maxX ?? 8);
      const nose = present ? train.position + (train.direction * length) / 2 : mouth;
      headlights.position.x = nose;
      // The beam is aimed in the rig's own space, so only its sign changes with
      // the direction of travel.
      beam.target.position.x = train.direction * 8;
      beam.target.updateMatrixWorld();

      // Bright enough to be the thing you notice. This is the only warning the
      // game gives before the pit becomes lethal, and a subtle one is no warning
      // at all to a player who is looking at their own fighter.
      beam.intensity = 180 * glow;
      lampMaterial.emissiveIntensity = 1 + 6 * glow;
      for (const lamp of lamps) lamp.rotation.y = train.direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    },

    dispose() {
      for (const item of disposables) item.dispose?.();
      scene.removeFromParent();
    },
  };
}
